import { useState } from 'react';
import { Plus, Search, Users } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useCreateCustomerMutation, useCustomerGroupsQuery, useCustomersQuery, type Customer,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';
import { CustomerDetailModal } from './CustomerDetailModal';

const emptyForm = {
  name: '',
  code: '',
  customerType: 'INDIVIDUAL',
  groupId: '',
  phone: '',
  email: '',
  city: '',
  creditLimit: '',
  paymentTermDays: '',
  openingBalance: '',
};

export function CustomersPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'owing' | 'overLimit'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const { data, isLoading, isError, error, refetch, isFetching } = useCustomersQuery({
    page,
    limit: 25,
    search: search || undefined,
    owingOnly: filter === 'owing' || undefined,
    overLimitOnly: filter === 'overLimit' || undefined,
  });
  const { data: groups } = useCustomerGroupsQuery();
  const [createCustomer, createState] = useCreateCustomerMutation();

  const fieldErrors = apiFieldErrors(createState.error);

  const submit = async (): Promise<void> => {
    const created = await createCustomer({
      name: form.name,
      code: form.code || undefined,
      customerType: form.customerType,
      groupId: form.groupId || undefined,
      phone: form.phone || undefined,
      email: form.email || undefined,
      city: form.city || undefined,
      creditLimit: form.creditLimit ? Number(form.creditLimit) : undefined,
      paymentTermDays: form.paymentTermDays ? Number(form.paymentTermDays) : undefined,
      openingBalance: form.openingBalance ? Number(form.openingBalance) : undefined,
    })
      .unwrap()
      .catch(() => null);

    if (created) {
      setCreating(false);
      setForm(emptyForm);
      setOpenId(created.id);
    }
  };

  return (
    <>
      <PageHeader
        title="Customers"
        description="Who you sell to, what they owe, and the credit you have extended them."
        action={
          <Can permission={P.CUSTOMER_CREATE}>
            <Button icon={Plus} onClick={() => setCreating(true)}>New customer</Button>
          </Can>
        }
      />

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Customers', value: String(data.summary.count) },
            {
              label: 'Owed to us',
              value: formatMoney(String(data.summary.owed), format),
              hint: 'across these customers',
              tone: 'owed',
            },
            {
              label: 'Credit extended',
              value: formatMoney(String(data.summary.creditExtended), format),
              hint: 'total of their limits',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="min-w-56 flex-1">
            <Input
              label="Search"
              icon={Search}
              placeholder="Name, code, phone or email"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-full sm:w-56">
            <Select
              label="Show"
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value as typeof filter);
                setPage(1);
              }}
            >
              <option value="all">All customers</option>
              <option value="owing">Owing money</option>
              <option value="overLimit">At or over their limit</option>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={Users}
            title={search || filter !== 'all' ? 'Nothing matches' : 'No customers yet'}
            description={
              search || filter !== 'all'
                ? 'Try a different search or clear the filter.'
                : 'Add a customer to sell on credit and keep a statement.'
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Customer</Th>
                  <Th>Group</Th>
                  <Th>Phone</Th>
                  <Th numeric>Balance</Th>
                  <Th numeric>Credit left</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((customer: Customer) => (
                  <tr key={customer.id} className="hover:bg-sunken">
                    <Td>
                      <button
                        type="button"
                        className="text-left font-medium hover:underline"
                        onClick={() => setOpenId(customer.id)}
                      >
                        {customer.name}
                      </button>
                      <p className="text-xs text-muted">{customer.code}</p>
                    </Td>
                    <Td className="text-secondary">{customer.group?.name ?? '—'}</Td>
                    <Td className="text-secondary">{customer.phone ?? '—'}</Td>
                    <Td
                      numeric
                      className={Number(customer.balance) > 0 ? 'text-red-600' : 'text-secondary'}
                    >
                      {formatMoney(customer.balance, format)}
                    </Td>
                    <Td numeric className="text-secondary">
                      {customer.availableCredit === null
                        ? 'Cash only'
                        : formatMoney(customer.availableCredit, format)}
                    </Td>
                    <Td>
                      {!customer.isActive ? (
                        <Badge tone="neutral">Inactive</Badge>
                      ) : customer.isOverLimit ? (
                        <Badge tone="danger">Over limit</Badge>
                      ) : (
                        <Badge tone="success">Active</Badge>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New customer"
        description="Leave the code blank and one is allocated from the numbering sequence."
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              onClick={() => void submit()}
              loading={createState.isLoading}
              disabled={!form.name}
            >
              Create customer
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {createState.error && <Alert tone="danger">{apiErrorMessage(createState.error)}</Alert>}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Name"
              required
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              error={fieldErrors.name}
            />
            <Input
              label="Code"
              placeholder="Allocated automatically"
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value })}
              error={fieldErrors.code}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Type"
              value={form.customerType}
              onChange={(event) => setForm({ ...form, customerType: event.target.value })}
            >
              <option value="INDIVIDUAL">Individual</option>
              <option value="COMPANY">Company</option>
            </Select>
            <Select
              label="Group"
              value={form.groupId}
              onChange={(event) => setForm({ ...form, groupId: event.target.value })}
            >
              <option value="">No group</option>
              {groups?.map((group) => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </Select>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Input
              label="Phone"
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
              error={fieldErrors.phone}
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              error={fieldErrors.email}
            />
            <Input
              label="City"
              value={form.city}
              onChange={(event) => setForm({ ...form, city: event.target.value })}
            />
          </div>

          <Can permission={P.CUSTOMER_CREDIT_MANAGE}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Credit limit"
                type="number"
                min="0"
                step="0.01"
                value={form.creditLimit}
                onChange={(event) => setForm({ ...form, creditLimit: event.target.value })}
                hint="Leave blank for cash only."
                error={fieldErrors.creditLimit}
              />
              <Input
                label="Payment terms (days)"
                type="number"
                min="0"
                value={form.paymentTermDays}
                onChange={(event) => setForm({ ...form, paymentTermDays: event.target.value })}
              />
            </div>
          </Can>

          <Input
            label="Opening balance"
            type="number"
            step="0.01"
            value={form.openingBalance}
            onChange={(event) => setForm({ ...form, openingBalance: event.target.value })}
            hint="What they already owed. Posted to the statement as the first entry."
          />
        </div>
      </Modal>

      {openId && <CustomerDetailModal customerId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
